import { useEffect, useRef, useState } from 'react';

import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { Toolbar } from 'primereact/toolbar';
import { Badge } from 'primereact/badge';
import { Dialog } from 'primereact/dialog';
import { FileUpload } from 'primereact/fileupload';

import { defaultPageRowOptions, defaultRowOptions, paginatorLinks, showingEntries, SkeletonbodyTemplate, Skeletonitems } from '../../../appconfig/Settings';
import AppBreadcrumb from '../../../components/CustomeBreadcrumb/AppBreadcrumb';
import { getAllDetails, postAllData } from '../../../service/ProductService';
import { GET_INWARD_LIST, APPROVE_REJECT_INWARD, IMPORT_INWARD } from '../../../config/ApiConstant';
import axiosInstance from '../../../config/axiosInstance';
import { InwardListColumns } from '../../../appconfig/DatatableSetting';
import { formatDateTime } from '../../../appconfig/AppHelper';
import * as XLSX from 'xlsx';

export const InwardList = () => {
    document.title = 'Inward List | Karan Enterprise';

    const toast = useRef<any>(null);
    const fileUploadRef = useRef<any>(null);

    const [globalFilter, setGlobalFilter] = useState<any>(null);
    const [inwardList, setInwardList] = useState<any>([]);
    const [pageLoad, setPageLoad] = useState<boolean>(false);
    const [type, setType] = useState<any>({ name: 'QR', code: 'qr' });
    const [selectedInwardData, setSelectedInwardData] = useState<any>([]);
    const [confirmDialogVisible, setConfirmDialogVisible] = useState<boolean>(false);
    const [confirmAction, setConfirmAction] = useState<string>('');
    const [actionLoading, setActionLoading] = useState<boolean>(false);
    const [importModalVisible, setImportModalVisible] = useState<boolean>(false);
    const [uploadLoading, setUploadLoading] = useState<boolean>(false);
    const [importFile, setImportFile] = useState<any>(null);

    useEffect(() => {
        getInwardList();
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [type]);

    // Get inward list
    const getInwardList = async () => {
        setPageLoad(false);

        const response = await getAllDetails(GET_INWARD_LIST);
        // get response
        if (response) {
            const DataList = response?.data;
            if (DataList.length === 0) {
                setInwardList([]);
            } else {
                setInwardList(DataList);
            }
            setPageLoad(true);
        } else {
            setPageLoad(false);
            toast.current?.show({
                severity: 'error',
                summary: 'Alert',
                detail: 'Something went wrong, Please try again.'
            });
        }
    };

    // Open confirm dialog
    const openConfirmDialog = (action: string) => {
        setConfirmAction(action);
        setConfirmDialogVisible(true);
    };

    // Close confirm dialog
    const closeConfirmDialog = () => {
        setConfirmDialogVisible(false);
        setConfirmAction('');
    };

    // Handle approve/reject action
    const handleConfirmAction = async () => {
        try {
            setActionLoading(true);

            const ids = selectedInwardData.map((item: any) => item.id);

            const requestData = {
                ids: ids,
                status: confirmAction
            };

            const response = await postAllData(APPROVE_REJECT_INWARD, requestData);

            if (response) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: response.message || `Inward ${confirmAction} successfully`
                });
                closeConfirmDialog();
                setSelectedInwardData([]);
                getInwardList();
            }
        } catch (error: any) {
            toast.current?.show({
                severity: 'error',
                summary: 'Alert',
                detail: error.response?.data?.message || 'Something went wrong'
            });
        } finally {
            setActionLoading(false);
        }
    };

    // global search
    const leftToolbarTemplate = () => {
        return (
            <>
                <span className="search-bar mr-3">
                    <i className="pi pi-search"></i>
                    <InputText type="search" className="search-input" onInput={(e) => setGlobalFilter(e.currentTarget.value)} placeholder="Search..." />
                </span>
                {selectedInwardData.length > 0 ? (
                    <>
                        <Button icon="pi pi-check" label="Approve" className="p-button-rounded p-button-primary mr-3" onClick={() => openConfirmDialog('approved')} />
                        <Button icon="pi pi-times" label="Reject" className="p-button-rounded p-button-danger mr-3" onClick={() => openConfirmDialog('rejected')} />
                    </>
                ) : (
                    <></>
                )}
            </>
        );
    };

    const rightToolbarTemplate = () => {
        return (
            <>
                <Button label="Import" icon="pi pi-upload" className="p-button-warning" onClick={() => openImportModal()} />
            </>
        );
    };

    // Open import modal
    const openImportModal = () => {
        setImportFile(null);
        setImportModalVisible(true);
    };

    // Close import modal
    const closeImportModal = () => {
        setImportModalVisible(false);
        setImportFile(null);
        if (fileUploadRef.current) {
            fileUploadRef.current.clear();
        }
    };

    // On file upload
    const onUploadImportFile = async (e: any) => {
        try {
            let val = e.target.files[0] || '';
            setImportFile(val);
        } catch (error) {
            console.error('Failed to read file', error);
        } finally {
            setUploadLoading(false);
        }
    };

    const readFileAsArray = (file: File): Promise<Record<string, any>[]> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = (e) => {
                try {
                    const binaryStr = e.target?.result;
                    const workbook = XLSX.read(binaryStr, { type: 'binary' });

                    const sheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[sheetName];

                    const rows: any[] = XLSX.utils.sheet_to_json(worksheet, {
                        header: 1,
                        defval: ''
                    });

                    if (rows.length < 2) {
                        resolve([]);
                        return;
                    }

                    const headers = rows[0] as string[];
                    const dataRows = rows.slice(1);

                    const result = dataRows.map((row: any) => {
                        const obj: Record<string, any> = {};
                        headers.forEach((header, index) => {
                            obj[header] = row[index] ?? '';
                        });
                        return obj;
                    });

                    resolve(result);
                } catch (err) {
                    reject(err);
                }
            };

            reader.onerror = () => reject(reader.error);
            reader.readAsBinaryString(file);
        });
    };

    // On upload import submit
    const onSubmitImportUpload = async () => {
        try {
            setUploadLoading(true);
            const data: any = await readFileAsArray(importFile);

            if (data.length > 0) {
                let requestData: any = {
                    rolls: data
                };

                const response = await postAllData(IMPORT_INWARD, requestData);

                if (response) {
                    closeImportModal();
                    getInwardList();
                    toast.current?.show({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'File imported successfully'
                    });
                }
            } else {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Error ocurred while uploading csv/excel'
                });
            }
        } catch (error: any) {
            setUploadLoading(false);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.message || 'Failed to import file'
            });
        }
    };

    // for product template
    const productTemplate = (rowData: any) => {
        return <>{rowData?.product?.name}</>;
    };

    // for scanned by template
    const scannedByTemplate = (rowData: any) => {
        return (
            <>
                {rowData?.scanned_user?.first_name} {rowData?.scanned_user?.last_name}
            </>
        );
    };

    // for scanned ar template
    const scannedAtTemplate = (rowData: any) => {
        return <>{formatDateTime(rowData?.scanned_at)}</>;
    };

    // for approved by template
    const approvedByTemplate = (rowData: any) => {
        return (
            <>
                {rowData?.approved_user?.first_name} {rowData?.approved_user?.last_name}
            </>
        );
    };

    // for status template
    const statusTemplate = (rowData: any) => {
        return (
            <>
                <Badge severity={rowData?.status === 'pending' ? 'warning' : rowData?.status === 'approved' ? 'success' : 'danger'} value={rowData?.status.toUpperCase()} />
            </>
        );
    };

    return (
        <>
            <AppBreadcrumb currentMenu={'Inward'} pageName={'Inward List'} pageTitle={'Inward List'} />
            <div className="grid crud-demo expanded-table">
                <div className="col-12">
                    <div className="card">
                        <Toast ref={toast} />
                        <Toolbar left={leftToolbarTemplate} right={rightToolbarTemplate}></Toolbar>
                        {/* Datatable */}
                        {pageLoad ? (
                            <>
                                <DataTable
                                    className="datatable-responsive"
                                    value={inwardList}
                                    paginator={inwardList.length >= 10 && true}
                                    globalFilter={globalFilter}
                                    rows={defaultRowOptions}
                                    rowsPerPageOptions={defaultPageRowOptions}
                                    paginatorTemplate={paginatorLinks}
                                    currentPageReportTemplate={showingEntries}
                                    emptyMessage="No Inward Data Found"
                                    showGridlines
                                    stripedRows
                                    selectionMode="checkbox"
                                    selection={selectedInwardData}
                                    onSelectionChange={(e) => setSelectedInwardData(e.value)}
                                >
                                    <Column selectionMode="multiple" headerStyle={{ width: '3rem' }}></Column>
                                    {InwardListColumns.map((col, i) => {
                                        if (col.field === 'product_id') {
                                            return <Column key={col.field} field={col.field} header={col.header} body={productTemplate} />;
                                        } else if (col.field === 'scanned_by') {
                                            return <Column key={col.field} field={col.field} header={col.header} body={scannedByTemplate} />;
                                        } else if (col.field === 'approved_by') {
                                            return <Column key={col.field} field={col.field} header={col.header} body={approvedByTemplate} />;
                                        } else if (col.field === 'status') {
                                            return <Column key={col.field} field={col.field} header={col.header} body={statusTemplate} />;
                                        } else if (col.field === 'scanned_at') {
                                            return <Column key={col.field} field={col.field} header={col.header} body={scannedAtTemplate} />;
                                        } else {
                                            return <Column key={col.field} field={col.field} header={col.header} filter />;
                                        }
                                    })}
                                </DataTable>
                            </>
                        ) : (
                            <>
                                {/* Skeleton Data table */}
                                <DataTable value={Skeletonitems}>
                                    {InwardListColumns.map((col, i) => (
                                        <Column key={col.field} field={col.field} header={col.header} body={SkeletonbodyTemplate} />
                                    ))}
                                </DataTable>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <Dialog
                visible={confirmDialogVisible}
                style={{ width: '450px' }}
                header="Confirm Action"
                modal
                footer={
                    <>
                        <Button label="Cancel" icon="pi pi-times" className="p-button-secondary mr-3" onClick={closeConfirmDialog} disabled={actionLoading} />
                        <Button label={confirmAction === 'approved' ? 'Approve' : 'Reject'} icon="pi pi-check" loading={actionLoading} onClick={handleConfirmAction} className={confirmAction === 'approved' ? 'p-button-primary' : 'p-button-danger'} />
                    </>
                }
                onHide={closeConfirmDialog}
            >
                <div className="flex align-items-center justify-content-center">
                    <i className={`pi ${confirmAction === 'approved' ? 'pi-check-circle' : 'pi-times-circle'} mr-3`} style={{ fontSize: '2rem', color: confirmAction === 'approved' ? '#22c55e' : '#ef4444' }} />
                    <span>
                        Are you sure you want to {confirmAction} {selectedInwardData.length} selected item{selectedInwardData.length > 1 ? 's' : ''}?
                    </span>
                </div>
            </Dialog>

            <Dialog
                visible={importModalVisible}
                style={{ width: '500px' }}
                header="Import Inward Data"
                modal
                footer={
                    <>
                        <Button label="Cancel" icon="pi pi-times" className="p-button-secondary" onClick={closeImportModal} disabled={uploadLoading} />
                        <Button label="Confirm" icon="pi pi-check" className="p-button-primary" onClick={onSubmitImportUpload} loading={uploadLoading} />
                    </>
                }
                onHide={closeImportModal}
            >
                <div className="p-fluid">
                    <div className="field">
                        <p className="mb-3">Upload CSV or Excel file to import inward data</p>
                        <div className="field col-12 md:col-12">
                            <label htmlFor="other_img">Other File Below </label>
                            <div className="upload-box">
                                <i className="pi pi-upload" style={{ fontSize: '1.2rem', marginRight: '0.5rem' }}></i>
                                <span>Upload</span>
                                <input type="file" id="fileInput" accept=".csv,.xlsx" onChange={(e) => onUploadImportFile(e)} className="file-input" />
                            </div>

                            {importFile && importFile instanceof File && (
                                <div className="file-preview">
                                    <div className="file-info">
                                        <a href={URL.createObjectURL(importFile)} target="_blank" rel="noopener noreferrer" className="file-name">
                                            {importFile.name}
                                        </a>
                                        <span className="file-details">
                                            {importFile.type} &nbsp;♦&nbsp; {(importFile.size / 1024).toFixed(2)} KB
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </Dialog>
        </>
    );
};
